import { Link } from 'react-router-dom'
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { ArrowRight } from "lucide-react"
import { motion, useScroll, useTransform } from "framer-motion"
import { useRef } from "react"

// Import SVG decorations
import ellipse29 from "@/assets/decorations/Ellipse 29.svg"
import ellipse30 from "@/assets/decorations/Ellipse 30.svg"
import ellipse31 from "@/assets/decorations/Ellipse 31.svg"
import ellipse32 from "@/assets/decorations/Ellipse 32.svg"
import ellipse33 from "@/assets/decorations/Ellipse 33.svg"
import line4 from "@/assets/decorations/Line 4.svg"
import line5 from "@/assets/decorations/Line 5.svg"
import line6 from "@/assets/decorations/Line 6.svg"
import line7 from "@/assets/decorations/Line 7.svg"
import line8 from "@/assets/decorations/Line 8.svg"
import line10 from "@/assets/decorations/Line 10.svg"
import line24 from "@/assets/decorations/Line 24.svg"
import line27 from "@/assets/decorations/Line 27.svg"
import favicon from "/hero-head-transparent.png"

export function Hero() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  })

  // Transform values for decorative elements
  const circleY = useTransform(scrollYProgress, [0, 1], [0, 100])
  const circleRotate = useTransform(scrollYProgress, [0, 1], [0, 45])
  const lineY = useTransform(scrollYProgress, [0, 1], [0, -80])
  const diagRotate = useTransform(scrollYProgress, [0, 1], [0, 30])
  const bottomCircleX = useTransform(scrollYProgress, [0, 1], [0, 60])
  const bottomCircleRotate = useTransform(scrollYProgress, [0, 1], [0, -30])

  // Floating decoration group transforms
  const floatY = useTransform(scrollYProgress, [0, 0.5, 1], [0, -30, -60])
  const floatOpacity = useTransform(scrollYProgress, [0, 0.3, 0.5], [1, 0.7, 0.5])
  const floatScale = useTransform(scrollYProgress, [0, 1], [1, 0.8])

  // Animation for favicon
  const iconScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.15])
  const iconRotate = useTransform(scrollYProgress, [0, 0.2], [0, 5])

  return (
    <div ref={containerRef} className="w-full relative overflow-hidden">
      {/* Decorative elements */}
      <motion.img
        src={ellipse29}
        alt=""
        className="absolute top-10 left-8 md:left-20 w-10 h-10 md:w-12 md:h-12"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{ 
          y: circleY,
          rotate: circleRotate
        }}
      />

      <motion.img
        src={line10}
        alt=""
        className="absolute bottom-20 right-6 md:right-24 w-8 h-8 md:w-10 md:h-10"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.8, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          y: lineY
        }}
      />

      <motion.img
        src={line4}
        alt=""
        className="absolute top-1/3 right-12 md:right-32 w-8 h-8 md:w-10 md:h-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 0.7, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        style={{
          rotate: diagRotate
        }}
      />

      {/* Floating decoration group */}
      <motion.div
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{
          y: floatY,
          opacity: floatOpacity,
          scale: floatScale
        }}
      >
        {/* Decorative circle 1 */}
        <motion.img
          src={ellipse30}
          alt=""
          className="absolute top-[15%] left-[15%] w-6 h-6"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.6, scale: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        />

        {/* Decorative line 1 */}
        <motion.img
          src={line5}
          alt=""
          className="absolute top-[30%] right-[15%] w-5 h-5"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.5, scale: 1, rotate: 45 }}
          transition={{ duration: 1, delay: 0.7 }}
        />

        {/* Decorative circle 2 */}
        <motion.img
          src={ellipse31}
          alt=""
          className="absolute bottom-[25%] left-[25%] w-7 h-7"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.4, scale: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
        />

        {/* Decorative line 2 */}
        <motion.img
          src={line6}
          alt=""
          className="absolute top-[60%] right-[30%] w-4 h-4"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.6, scale: 1, rotate: -30 }}
          transition={{ duration: 1, delay: 1.1 }}
        />
      </motion.div>

      <Card className="bg-neutral-950 text-neutral-50 border-none rounded-none">
        <CardContent className="px-4 py-16 md:p-16 lg:p-24 flex flex-col items-center text-center space-y-6 md:space-y-8 relative">
          <motion.h1 
            className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl xl:text-7xl/none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Polymath Solutions
            <br />
            Holding Group
          </motion.h1>

          {/* Favicon between title and paragraph - MUCH BIGGER */}
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            style={{
              scale: iconScale,
              rotate: iconRotate
            }}
            className="w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 lg:w-80 lg:h-80 my-8 md:my-12"
          >
            <motion.img
              src={favicon}
              alt="PSHG Logo"
              className="w-full h-full object-contain"
              animate={{
                scale: [1, 1.08, 1],
                transition: {
                  duration: 5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }
              }}
            />
          </motion.div>

          <motion.p 
            className="mx-auto max-w-[700px] text-base md:text-lg text-neutral-300"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Bridging innovation across investment, consulting, and community impact. 
            We combine financial expertise with technological advancement to create 
            sustainable value.
          </motion.p>

          <motion.div 
            className="flex flex-col sm:flex-row justify-center gap-3 md:gap-4 w-full sm:w-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Link to="/investment" className="w-full sm:w-auto">
              <Button size="lg" className="bg-white text-neutral-950 hover:bg-neutral-200 w-full sm:w-auto">
                Our Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/about" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto">
                Learn More
              </Button>
            </Link>
          </motion.div>

          {/* Additional decorations */}
          <motion.img
            src={ellipse32}
            alt=""
            className="absolute bottom-8 left-1/4 w-6 h-6 md:w-8 md:h-8 opacity-60"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.6, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            style={{
              x: bottomCircleX,
              rotate: bottomCircleRotate
            }}
          />

          {/* Add more decorative elements */}
          <motion.img
            src={line7}
            alt=""
            className="absolute top-20 left-1/2 w-6 h-6 opacity-40"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.4, scale: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            style={{
              rotate: useTransform(scrollYProgress, [0, 1], [0, 60])
            }}
          />

          <motion.img
            src={ellipse33}
            alt=""
            className="absolute bottom-32 right-1/2 w-8 h-8 opacity-30"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.3, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            style={{
              y: useTransform(scrollYProgress, [0, 1], [0, 40])
            }}
          />

          {/* Additional decorations at different positions */}
          <motion.img
            src={line8}
            alt=""
            className="absolute top-40 right-[15%] w-5 h-5 opacity-20"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.2, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.4 }}
            style={{
              rotate: useTransform(scrollYProgress, [0, 1], [0, -45])
            }}
          />

          <motion.img
            src={line24}
            alt=""
            className="absolute bottom-16 right-[20%] w-6 h-6 opacity-30"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.3, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.6 }}
            style={{
              y: useTransform(scrollYProgress, [0, 1], [0, -25])
            }}
          />

          <motion.img
            src={line27}
            alt=""
            className="absolute bottom-40 left-[20%] w-5 h-5 opacity-25"
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 0.25, scale: 1 }}
            transition={{ duration: 0.8, delay: 1.8 }}
            style={{
              x: useTransform(scrollYProgress, [0, 1], [0, 40])
            }}
          />
        </CardContent>
      </Card>
    </div>
  )
} 